import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  Building2, 
  Search,
  Filter,
  Clock,
  CheckCircle,
  XCircle,
  DollarSign,
  MapPin,
  Calendar,
  ArrowRight,
  Send,
  Phone,
  Mail,
  User,
  Eye,
  Play,
  FileText
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function HoldingStillastjenester() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [quoteForm, setQuoteForm] = useState({
    assembly_cost: "",
    rental_cost_per_week: "",
    estimated_weeks: "",
    disassembly_cost: "",
    equipment_cost: "0",
    other_costs: "0",
    description: "",
    valid_until: "",
    payment_terms: "50_50",
    message: ""
  });

  useEffect(() => {
    fetchRequests();
  }, [token]);

  useEffect(() => {
    filterRequests();
  }, [requests, searchQuery, statusFilter]);

  const fetchRequests = async () => {
    try {
      const res = await fetch(`${API_URL}/api/projects-v3/stillastjenester/requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setRequests(await res.json());
      }
    } catch (error) {
      console.error("Error fetching requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterRequests = () => {
    let filtered = [...requests];
    
    if (statusFilter !== "all") {
      filtered = filtered.filter(r => r.status === statusFilter);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r => 
        r.title?.toLowerCase().includes(query) ||
        r.contact_name?.toLowerCase().includes(query) ||
        r.location_city?.toLowerCase().includes(query)
      );
    }
    
    setFilteredRequests(filtered);
  };

  const handleSendQuote = async () => {
    if (!quoteForm.assembly_cost || !quoteForm.rental_cost_per_week || !quoteForm.estimated_weeks || !quoteForm.disassembly_cost) {
      toast.error("Vennligst fyll ut alle obligatoriske felt");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/projects-v3/stillastjenester/${selectedRequest.quote_request_id}/quote`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          assembly_cost: parseFloat(quoteForm.assembly_cost),
          rental_cost_per_week: parseFloat(quoteForm.rental_cost_per_week),
          estimated_weeks: parseInt(quoteForm.estimated_weeks),
          disassembly_cost: parseFloat(quoteForm.disassembly_cost),
          equipment_cost: parseFloat(quoteForm.equipment_cost) || 0,
          other_costs: parseFloat(quoteForm.other_costs) || 0,
          description: quoteForm.description,
          valid_until: quoteForm.valid_until,
          payment_terms: quoteForm.payment_terms,
          message: quoteForm.message
        })
      });

      if (!res.ok) throw new Error("Kunne ikke sende tilbud");

      const data = await res.json();
      toast.success(`Tilbud sendt! Total: ${formatCurrency(data.total_with_vat)}`);
      setShowQuoteModal(false);
      fetchRequests();
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleMakeLive = async (request) => {
    if (!request.project_id) {
      toast.error("Ingen prosjekt knyttet til denne forespørselen");
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/projects-v3/stillastjenester/${request.quote_request_id}/make-live`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error("Kunne ikke aktivere prosjekt");

      toast.success("Prosjekt er nå live for team-søknader!");
      fetchRequests();
      
    } catch (error) {
      toast.error(error.message);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("no-NO", {
      style: "currency",
      currency: "NOK",
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("no-NO", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending_quote: { label: "Venter på tilbud", color: "bg-yellow-500/20 text-yellow-400", icon: Clock },
      quote_sent: { label: "Tilbud sendt", color: "bg-blue-500/20 text-blue-400", icon: Send },
      negotiating: { label: "Forhandling", color: "bg-orange-500/20 text-orange-400", icon: DollarSign },
      quote_accepted: { label: "Akseptert", color: "bg-green-500/20 text-green-400", icon: CheckCircle },
      project_live: { label: "Prosjekt live", color: "bg-cyan-500/20 text-cyan-400", icon: Play },
      rejected: { label: "Avslått", color: "bg-red-500/20 text-red-400", icon: XCircle }
    };
    return configs[status] || { label: status, color: "bg-gray-500/20 text-gray-400", icon: Clock };
  };

  const openQuoteModal = (request) => {
    setSelectedRequest(request);
    setQuoteForm({
      assembly_cost: "",
      rental_cost_per_week: "",
      estimated_weeks: request.estimated_duration_weeks || "",
      disassembly_cost: "",
      equipment_cost: "0",
      other_costs: "0",
      description: "",
      valid_until: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
      payment_terms: "50_50",
      message: ""
    });
    setShowQuoteModal(true);
  };

  const calculateTotal = () => {
    const assembly = parseFloat(quoteForm.assembly_cost) || 0;
    const rental = (parseFloat(quoteForm.rental_cost_per_week) || 0) * (parseInt(quoteForm.estimated_weeks) || 0);
    const disassembly = parseFloat(quoteForm.disassembly_cost) || 0;
    const equipment = parseFloat(quoteForm.equipment_cost) || 0;
    const other = parseFloat(quoteForm.other_costs) || 0;
    const subtotal = assembly + rental + disassembly + equipment + other;
    const vat = subtotal * 0.25;
    return { subtotal, vat, total: subtotal + vat };
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6" data-testid="holding-stillastjenester">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Stillastjenester</h1>
            <p className="text-gray-400 mt-1">
              Administrer forespørsler om komplette stillasløsninger
            </p>
          </div>
        </div>

        {/* Filters */}
        <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Søk etter forespørsel..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-[#0f0f1a] border-[#2a2a4a] text-white"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full md:w-[200px] bg-[#0f0f1a] border-[#2a2a4a] text-white">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter status" />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a2e] border-[#2a2a4a]">
                  <SelectItem value="all">Alle statuser</SelectItem>
                  <SelectItem value="pending_quote">Venter på tilbud</SelectItem>
                  <SelectItem value="quote_sent">Tilbud sendt</SelectItem>
                  <SelectItem value="negotiating">Forhandling</SelectItem>
                  <SelectItem value="quote_accepted">Akseptert</SelectItem>
                  <SelectItem value="project_live">Prosjekt live</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-yellow-400">
                {requests.filter(r => r.status === "pending_quote").length}
              </p>
              <p className="text-gray-400 text-sm">Venter tilbud</p>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-400">
                {requests.filter(r => r.status === "quote_sent").length}
              </p>
              <p className="text-gray-400 text-sm">Tilbud sendt</p>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-400">
                {requests.filter(r => r.status === "quote_accepted").length}
              </p>
              <p className="text-gray-400 text-sm">Akseptert</p>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-cyan-400">
                {requests.filter(r => r.status === "project_live").length}
              </p>
              <p className="text-gray-400 text-sm">Prosjekt live</p>
            </CardContent>
          </Card>
        </div>

        {/* Requests List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-gray-400 mt-4">Laster forespørsler...</p>
          </div>
        ) : filteredRequests.length === 0 ? (
          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardContent className="p-12 text-center">
              <Building2 className="w-16 h-16 mx-auto text-gray-600 mb-4" />
              <h3 className="text-white font-semibold mb-2">Ingen forespørsler</h3>
              <p className="text-gray-400">
                {searchQuery || statusFilter !== "all" 
                  ? "Prøv å endre søk eller filter"
                  : "Det er ingen forespørsler om stillastjenester ennå"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredRequests.map((request) => {
              const statusConfig = getStatusConfig(request.status);
              const StatusIcon = statusConfig.icon;
              const latestQuote = request.quotes?.[request.quotes.length - 1];
              
              return (
                <Card 
                  key={request.quote_request_id} 
                  className="bg-[#1a1a2e] border-[#2a2a4a] hover:border-[#23F7DD]/50 transition-colors"
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                      {/* Left - Request Info */}
                      <div className="flex-1">
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-lg ${statusConfig.color} flex items-center justify-center flex-shrink-0`}>
                            <StatusIcon className="w-5 h-5" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="text-white font-semibold">{request.title}</h3>
                              <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                              <Badge variant="outline" className="border-[#2a2a4a] text-gray-400">
                                {request.client_type === "private" ? "Privat" : "Bedrift"}
                              </Badge>
                            </div>
                            
                            <p className="text-gray-400 text-sm mt-1 line-clamp-2">{request.description}</p>
                            
                            <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-gray-500">
                              <div className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                <span>{request.location_address}, {request.location_city}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                <span>Ønsket: {formatDate(request.desired_start_date)}</span>
                              </div>
                            </div>
                            
                            {/* Contact Info */}
                            <div className="flex flex-wrap items-center gap-4 mt-2 text-sm">
                              <div className="flex items-center gap-1 text-[#23F7DD]">
                                <User className="w-4 h-4" />
                                <span>{request.contact_name}</span>
                              </div>
                              <div className="flex items-center gap-1 text-gray-400">
                                <Phone className="w-4 h-4" />
                                <span>{request.contact_phone}</span>
                              </div>
                              <div className="flex items-center gap-1 text-gray-400">
                                <Mail className="w-4 h-4" />
                                <span>{request.contact_email}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Right - Quote & Actions */}
                      <div className="flex flex-col items-end gap-3">
                        {latestQuote && (
                          <div className="text-right">
                            <p className="text-[#23F7DD] font-bold text-xl">
                              {formatCurrency(latestQuote.total_with_vat)}
                            </p>
                            <p className="text-gray-500 text-sm">Tilbud sendt</p>
                          </div>
                        )}
                        
                        <div className="flex gap-2">
                          {request.status === "pending_quote" && (
                            <Button 
                              onClick={() => openQuoteModal(request)}
                              className="bg-[#23F7DD] hover:bg-[#1de9cf] text-black"
                            >
                              <Send className="w-4 h-4 mr-2" />
                              Send tilbud
                            </Button>
                          )}
                          
                          {request.status === "quote_accepted" && (
                            <Button 
                              onClick={() => handleMakeLive(request)}
                              className="bg-green-500 hover:bg-green-600 text-white"
                            >
                              <Play className="w-4 h-4 mr-2" />
                              Aktiver prosjekt
                            </Button>
                          )}
                          
                          {request.project_id && (
                            <Link to={`/holding/projects/${request.project_id}`}>
                              <Button variant="outline" className="border-[#2a2a4a] text-gray-300">
                                <Eye className="w-4 h-4 mr-2" />
                                Se prosjekt
                              </Button>
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Quote Modal */}
        <Dialog open={showQuoteModal} onOpenChange={setShowQuoteModal}>
          <DialogContent className="bg-[#1a1a2e] border-[#2a2a4a] max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#23F7DD]" />
                Send tilbud - {selectedRequest?.title}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Montering (NOK) *</Label>
                  <Input
                    type="number"
                    value={quoteForm.assembly_cost}
                    onChange={(e) => setQuoteForm({ ...quoteForm, assembly_cost: e.target.value })}
                    placeholder="0"
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-gray-300">Demontering (NOK) *</Label>
                  <Input
                    type="number"
                    value={quoteForm.disassembly_cost}
                    onChange={(e) => setQuoteForm({ ...quoteForm, disassembly_cost: e.target.value })}
                    placeholder="0"
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-gray-300">Leie per uke (NOK) *</Label>
                  <Input
                    type="number"
                    value={quoteForm.rental_cost_per_week}
                    onChange={(e) => setQuoteForm({ ...quoteForm, rental_cost_per_week: e.target.value })}
                    placeholder="0"
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-gray-300">Antall uker *</Label>
                  <Input
                    type="number"
                    min={1}
                    value={quoteForm.estimated_weeks}
                    onChange={(e) => setQuoteForm({ ...quoteForm, estimated_weeks: e.target.value })}
                    placeholder="1"
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-gray-300">Utstyr (NOK)</Label>
                  <Input
                    type="number"
                    value={quoteForm.equipment_cost}
                    onChange={(e) => setQuoteForm({ ...quoteForm, equipment_cost: e.target.value })}
                    placeholder="0"
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-gray-300">Andre kostnader (NOK)</Label>
                  <Input
                    type="number"
                    value={quoteForm.other_costs}
                    onChange={(e) => setQuoteForm({ ...quoteForm, other_costs: e.target.value })}
                    placeholder="0"
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                  />
                </div>
                
                <div className="col-span-2">
                  <Label className="text-gray-300">Beskrivelse av tilbud</Label>
                  <Textarea
                    value={quoteForm.description}
                    onChange={(e) => setQuoteForm({ ...quoteForm, description: e.target.value })}
                    placeholder="Detaljer om tilbudet..."
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-gray-300">Gyldig til *</Label>
                  <Input
                    type="date"
                    value={quoteForm.valid_until}
                    onChange={(e) => setQuoteForm({ ...quoteForm, valid_until: e.target.value })}
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-gray-300">Betalingsbetingelser</Label>
                  <Select 
                    value={quoteForm.payment_terms} 
                    onValueChange={(v) => setQuoteForm({ ...quoteForm, payment_terms: v })}
                  >
                    <SelectTrigger className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a2e] border-[#2a2a4a]">
                      <SelectItem value="50_50">50% ved start, 50% ved slutt</SelectItem>
                      <SelectItem value="full_on_completion">100% ved ferdigstillelse</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Price Summary */}
              <div className="bg-[#0f0f1a] rounded-xl p-4 mt-4">
                <h4 className="text-gray-400 text-sm mb-3">Prissammendrag</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Subtotal</span>
                    <span className="text-white">{formatCurrency(calculateTotal().subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">MVA (25%)</span>
                    <span className="text-white">{formatCurrency(calculateTotal().vat)}</span>
                  </div>
                  <div className="flex justify-between border-t border-[#2a2a4a] pt-2 mt-2">
                    <span className="text-white font-semibold">Total inkl. MVA</span>
                    <span className="text-[#23F7DD] font-bold text-lg">{formatCurrency(calculateTotal().total)}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowQuoteModal(false)} className="text-gray-400">
                Avbryt
              </Button>
              <Button 
                onClick={handleSendQuote}
                disabled={submitting}
                className="bg-[#23F7DD] hover:bg-[#1de9cf] text-black"
              >
                {submitting ? "Sender..." : "Send tilbud"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
